import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxButtonModule } from 'devextreme-angular';
import { ActivatedRoute, NavigationStart, Router, RouterModule } from '@angular/router';
import { GridConnectionService } from 'src/app/services/grid-connection.service';
import { AppGlobals } from 'src/app/app.globals';
import { filter } from 'rxjs';
import { NavigationEnd } from '@angular/router';
import { environment } from 'src/environments/environment';
import { FeatureFlags } from 'src/app/interfaces/enums/feature-flag';
import { Subject, takeUntil } from 'rxjs';
import { HeaderTab } from 'src/app/components/page-nav-bar/page-nav-bar.component';
import { PageNavBarComponent } from 'src/app/components/page-nav-bar/page-nav-bar.component';
import { PageNavigationService } from 'src/app/services/page-navigation.service';

@Component({
    selector: 'app-grid-connection',
    imports: [CommonModule, DxButtonModule, RouterModule, PageNavBarComponent],
    templateUrl: './grid-connection.page.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./grid-connection.page.css']
})
export class GridConnectionComponent implements OnInit, OnDestroy {
  projectSecondaryTabs: HeaderTab[] = [];
  public static GridConnectionComponentInstance: GridConnectionComponent = null;

  currentRoute: any;
  maxTabLength: number = 0;
  commentCount: number = 0;
  gridConnectionId: number = null;
  tabsVisible: boolean = true;

  activeHeaderTab: any;
  selectedProjectId: any = 0;

  /** Both the Project tab and the Site Contracts tab manage their own inner padding further
   *  down (project.page's .gpd-content-container, and each of contract-overview/
   *  contract-deadlines/contract-calendar's own container) — so .gpd-inner-page's own 15px
   *  padding here would just stack a second inset on top of theirs. Flushing it for both routes
   *  leaves each page's own padding as the only one that actually applies. */
  get isInnerPageFlush(): boolean {
    return this.router.url.includes('project') || this.router.url.includes('site-contracts');
  }

  _subscription: any;

  tabs: any = [
    //{ text: $localize`:@@siteOverview:Site Overview`, routerLink: 'overview' },
    // { text: $localize`:@@properties:Properties`, routerLink: 'properties' },
    // { text: $localize`Measurement Concept`, routerLink: 'measurementConcept' },
    // { text: $localize`:@@microtasks:Microtasks`, routerLink: 'microtasks' },
    // { text: $localize`:@@comments:Comments`, routerLink: 'gridConnectionComments' },
    // { text: $localize`:@@documents:Documents`, routerLink: 'gridConnectionDocuments' },
    // { text: $localize`:@@infrastructure:Infrastructure`, routerLink: 'gridConnectionComponents' }
  ];

  headerTabs: HeaderTab[] = [
    { id: 1, text: $localize`:@@site:Site`, icon: 'location', routerLink: 'site-overview' },
    { id: 2, text: $localize`:@@project:Project`, icon: 'folder-library', routerLink: 'project' + '/' + 0, disabled: this.selectedProjectId == 0 },
    { id: 3, text: $localize`:@@properties:Properties`, icon: 'home', routerLink: 'properties', hasNoPermission: !AppGlobals.UserCan(FeatureFlags.Property) },
    { id: 4, text: $localize`:@@infrastructure:Infrastructure`, icon: 'layers', routerLink: 'infrastructure', hasNoPermission: !AppGlobals.UserCan(FeatureFlags.TechnicalComponents) },
    { id: 5, text: $localize`:@@contracts:Contracts`, icon: 'contracts', routerLink: 'site-contracts', hasNoPermission: !AppGlobals.UserCan(FeatureFlags.Contracts) },
  ];

  private $unsubscribe = new Subject();

  constructor(
    private appGlobals: AppGlobals,
    private gridConnectionService: GridConnectionService,
    private activatedRout: ActivatedRoute,
    private router: Router,
    private pageNavService: PageNavigationService
  ) {
    if (GridConnectionComponent.GridConnectionComponentInstance == null)
      GridConnectionComponent.GridConnectionComponentInstance = this;
    this.selectedProjectId = this.gridConnectionService.currentSelectedProjectId;
  }
  ngOnInit(): void {
    if (environment.isTestEnvironment === true) {
      this.appGlobals.SetGlobalMenuIndex(1);
    } else {
      this.appGlobals.SetGlobalMenuIndex(1);
    }

    this.appGlobals.$features.pipe(takeUntil(this.$unsubscribe)).subscribe(() => {
      const technicalComponentTab = this.headerTabs.find(project => project.routerLink === 'infrastructure');
      if (!technicalComponentTab) {
        return;
      }

      technicalComponentTab.hasNoPermission = !AppGlobals.UserCan(FeatureFlags.TechnicalComponents);
      this.headerTabs = [...this.headerTabs];
    });

    this.pageNavService.secondaryTabs$
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe(tabs => { this.projectSecondaryTabs = tabs; });

    this.tabsVisible = true;
    this.calculateMaxTabLength();
    this.gridConnectionId = Number(this.activatedRout.snapshot.params.id);

    this.activatedRout.paramMap
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe(params => {
        const newId = Number(params.get('id'));
        if (newId && newId !== this.gridConnectionId) {
          this.gridConnectionId = newId;
          if (!this.router.url.includes('project')) {
            this.updateGridConnectionTitle();
          }
        }
      });

    this.gridConnectionService.currentSelectedProjectIdChange.subscribe(value => {
      this.headerTabs[1].routerLink = 'project' + '/' + value;
      this.headerTabs[1].disabled = false;
    });

    if (this.router.url.includes('site-overview')) {
      this.activeHeaderTab = 1;
    }
    if (this.router.url.includes('project')) {
      this.activeHeaderTab = 2;
    }
    if (this.router.url.includes('properties')) {
      this.activeHeaderTab = 3;
    }
    if (this.router.url.includes('infrastructure')) {
      this.activeHeaderTab = 4;
    }

    // Subscribe to all navigation events
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd || event instanceof NavigationStart),
        takeUntil(this.$unsubscribe)
      )
      .subscribe(val => {
        if (val instanceof NavigationStart) {
          if (val.url.includes('site-overview')) {
            this.activeHeaderTab = 1;
          } else if (val.url.includes('project')) {
            this.activeHeaderTab = 2;
          } else if (val.url.includes('properties')) {
            this.activeHeaderTab = 3;
          } else if (val.url.includes('infrastructure')) {
            this.activeHeaderTab = 4;
          }
        }

        // When navigation completes, update grid connection title
        if (val instanceof NavigationEnd) {
          this.RefreshCommentTabs();

          if (!val.url.includes('/project/')) {
            this.updateGridConnectionTitle();
          }
        }
      });

    // Initial title update — only set site title when not inside a project sub-route
    if (!this.router.url.includes('project')) {
      this.updateGridConnectionTitle();
    }
    this.RefreshCommentTabs();
  }

  // Extract title fetching logic to a separate method
  private updateGridConnectionTitle(): void {
    // Make sure we have a valid ID before making the API call
    if (this.gridConnectionId) {
      this.gridConnectionService.getGridConnectionTitle({ GridConnectionId: this.gridConnectionId }).then(
        result => {
          if (result.hasError === true) AppGlobals.ShowErrorMessage(result.feedback);
          else {
            const { nr, title } = result.gridConnectionTitleItem;
            this.appGlobals.$headerTitle.next(`${nr} ${title}`);
            const siteLabel = nr && title ? `${nr} | ${title}` : (title ?? nr ?? null);
            this.appGlobals.$currentSiteName.next(siteLabel);
          }
        },
        (error: any) => {
          AppGlobals.ShowErrorMessage(error);
        }
      );
    }
  }

  ngOnDestroy(): void {
    this.$unsubscribe.next(undefined);
    this.$unsubscribe.complete();
  }

  calculateMaxTabLength() {
    this.maxTabLength = Math.max(...this.tabs.map(tab => tab.text.length));
  }

  getIcon(item: any): string {
    return 'user';
  }

  handleTabLoad(e: any, index: number): void {
    if (index != 2) return;
    if (this.commentCount === 0) return;
    e.element.children[0].innerHTML += `<span class='tab-comment-counter' >${this.commentCount}</span>`;
  }

  public RefreshCommentTabs() {
    this.tabsVisible = false;
  }

  checkIfVisible() {
    let route = this.router.url;

    if (route.includes('site-overview') || route.includes('properties') || route.includes('infrastructure')) {
      return false;
    }

    return true;
  }

  setActiveHeaderTab(id: any) {
    this.activeHeaderTab = id;
  }

  checkIfActive(id): any {
    if (this.activeHeaderTab == id) {
      return true;
    }
  }

  trackItem(index: number, item: any) {
    return item.id;
  }
}
